import { createAsyncThunk } from "@reduxjs/toolkit";
import { AxiosInstance } from "axios";
import axiosInstance from "../../axiosInstance/axiosInstance";
import portalAxios from "../../portal/portalAxios";
import { ApiErrorResponse } from "../../../api/types/ApiErrorResponse";
import { toApiError } from "../../../api/utils/errorHandling";
import { PaginatedResult } from "../../sessions/contracts/PaginatedResult";
import { MessagePageResponse } from "../contracts/MessagePageResponse";
import { MessageResponse } from "../contracts/MessageResponse";
import { SendMessageRequest } from "../contracts/SendMessageRequest";
import { SessionCardResponse } from "../contracts/SessionCardResponse";
import { SetStaffOnlyRequest } from "../contracts/SetStaffOnlyRequest";
import { ThreadResponse } from "../contracts/ThreadResponse";
import { UnreadCountResponse } from "../contracts/UnreadCountResponse";
import { MessageResponseMapper } from "../mappers/MessageResponseMapper";
import { toSessionCard } from "../mappers/SessionCardResponseMapper";
import { ThreadResponseMapper } from "../mappers/ThreadResponseMapper";
import { Message } from "../models/Message";
import { MessagesChannel } from "../models/MessagesChannel";
import { MessageThread } from "../models/MessageThread";
import { SessionCard } from "../models/SessionCard";

const threadMapper = new ThreadResponseMapper();
const messageMapper = new MessageResponseMapper();

/** Newest messages fetched per request; the API caps it at 100. */
export const MESSAGE_PAGE_SIZE = 30;

/** The HTTP client and route prefix of a channel: staff token vs. portal token. */
const route = (channel: MessagesChannel): { http: AxiosInstance; base: string } =>
  channel.kind === "staff"
    ? { http: axiosInstance, base: "/messages" }
    : { http: portalAxios, base: `/portal/members/${channel.memberId}/messages` };

interface ThreadArgs {
  channel: MessagesChannel;
  threadId: string;
}

/** The reader's threads, latest first (staff: the organization's first 100; member: direct, group and Session). */
export const fetchThreads = createAsyncThunk<
  MessageThread[],
  MessagesChannel,
  { rejectValue: ApiErrorResponse }
>("messages/fetchThreads", async (channel, { rejectWithValue }) => {
  try {
    const { http, base } = route(channel);
    const params = channel.kind === "staff" ? { page: 1, pageSize: 100 } : undefined;
    const response = await http.get<PaginatedResult<ThreadResponse>>(`${base}/threads`, { params });
    return threadMapper.toThreads(response.data.items);
  } catch (error: unknown) {
    return rejectWithValue(toApiError(error, "Failed to load messages"));
  }
});

/** Staff only: the member's direct thread, created on first use. */
export const openDirectThread = createAsyncThunk<
  MessageThread,
  string,
  { rejectValue: ApiErrorResponse }
>("messages/openDirectThread", async (memberId, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<ThreadResponse>(`/messages/threads/direct/${memberId}`);
    return threadMapper.toThread(response.data);
  } catch (error: unknown) {
    return rejectWithValue(toApiError(error, "Failed to open the conversation"));
  }
});

/** Staff only: a Group's or a Session's thread, created on first use. */
export const openSubjectThread = createAsyncThunk<
  MessageThread,
  { kind: "group" | "session"; subjectId: string },
  { rejectValue: ApiErrorResponse }
>("messages/openSubjectThread", async ({ kind, subjectId }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<ThreadResponse>(`/messages/threads/${kind}/${subjectId}`);
    return threadMapper.toThread(response.data);
  } catch (error: unknown) {
    return rejectWithValue(toApiError(error, "Failed to open the conversation"));
  }
});

/** The Session card pinned above a Session thread. */
export const fetchSessionCard = createAsyncThunk<SessionCard, ThreadArgs, { rejectValue: ApiErrorResponse }>(
  "messages/fetchSessionCard",
  async ({ channel, threadId }, { rejectWithValue }) => {
    try {
      const { http, base } = route(channel);
      const response = await http.get<SessionCardResponse>(`${base}/threads/${threadId}/session-card`);
      return toSessionCard(response.data);
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error, "Failed to load the session"));
    }
  },
);

/** The newest page of a thread; merged into what is already loaded. */
export const fetchMessages = createAsyncThunk<
  { items: Message[]; nextBefore: string | null },
  ThreadArgs,
  { rejectValue: ApiErrorResponse }
>("messages/fetchMessages", async ({ channel, threadId }, { rejectWithValue }) => {
  try {
    const { http, base } = route(channel);
    const response = await http.get<MessagePageResponse>(`${base}/threads/${threadId}/messages`, {
      params: { pageSize: MESSAGE_PAGE_SIZE },
    });
    return { items: messageMapper.toMessages(response.data.items), nextBefore: response.data.nextBefore };
  } catch (error: unknown) {
    return rejectWithValue(toApiError(error, "Failed to load messages"));
  }
});

/** The page older than `before`, for scrolling back. */
export const fetchOlderMessages = createAsyncThunk<
  { items: Message[]; nextBefore: string | null },
  ThreadArgs & { before: string },
  { rejectValue: ApiErrorResponse }
>("messages/fetchOlderMessages", async ({ channel, threadId, before }, { rejectWithValue }) => {
  try {
    const { http, base } = route(channel);
    const response = await http.get<MessagePageResponse>(`${base}/threads/${threadId}/messages`, {
      params: { before, pageSize: MESSAGE_PAGE_SIZE },
    });
    return { items: messageMapper.toMessages(response.data.items), nextBefore: response.data.nextBefore };
  } catch (error: unknown) {
    return rejectWithValue(toApiError(error, "Failed to load messages"));
  }
});

/** Sends a message (line breaks kept) with its completed attachments. */
export const sendMessage = createAsyncThunk<
  Message,
  ThreadArgs & { body: string; attachmentIds?: string[] },
  { rejectValue: ApiErrorResponse }
>("messages/send", async ({ channel, threadId, body, attachmentIds }, { rejectWithValue }) => {
  try {
    const { http, base } = route(channel);
    const request: SendMessageRequest = attachmentIds?.length ? { body, attachmentIds } : { body };
    const response = await http.post<MessageResponse>(`${base}/threads/${threadId}/messages`, request);
    return messageMapper.toMessage(response.data);
  } catch (error: unknown) {
    return rejectWithValue(toApiError(error, "Failed to send the message"));
  }
});

/** Marks the thread read for the reader. */
export const markThreadRead = createAsyncThunk<void, ThreadArgs, { rejectValue: ApiErrorResponse }>(
  "messages/markRead",
  async ({ channel, threadId }, { rejectWithValue }) => {
    try {
      const { http, base } = route(channel);
      await http.post(`${base}/threads/${threadId}/read`);
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error, "Failed to mark the conversation read"));
    }
  },
);

/**
 * Mutes or unmutes a thread for the reader (no email nudge, no nav-badge count), then refreshes
 * the badge, which the server counts without muted threads.
 */
export const setThreadMuted = createAsyncThunk<void, ThreadArgs & { muted: boolean }, { rejectValue: ApiErrorResponse }>(
  "messages/setMuted",
  async ({ channel, threadId, muted }, { dispatch, rejectWithValue }) => {
    try {
      const { http, base } = route(channel);
      const url = `${base}/threads/${threadId}/mute`;
      await (muted ? http.post(url) : http.delete(url));
      void dispatch(fetchUnreadCount(channel));
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error, "Failed to change the mute"));
    }
  },
);

/**
 * Deletes a message: staff any, a member their own (or their ward's) within 24 h. The row stays as a
 * "Message deleted" placeholder; the live signal refreshes every other open view.
 */
export const deleteMessage = createAsyncThunk<void, ThreadArgs & { messageId: string }, { rejectValue: ApiErrorResponse }>(
  "messages/delete",
  async ({ channel, threadId, messageId }, { rejectWithValue }) => {
    try {
      const { http, base } = route(channel);
      await http.delete(`${base}/threads/${threadId}/messages/${messageId}`);
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error, "Failed to delete the message"));
    }
  },
);

/** Staff only: switches a Group or Session thread to announcements only, or opens it to members again. */
export const setThreadStaffOnly = createAsyncThunk<
  MessageThread,
  { threadId: string; staffOnly: boolean },
  { rejectValue: ApiErrorResponse }
>("messages/setStaffOnly", async ({ threadId, staffOnly }, { rejectWithValue }) => {
  try {
    const request: SetStaffOnlyRequest = { staffOnly };
    const response = await axiosInstance.put<ThreadResponse>(`/messages/threads/${threadId}/staff-only`, request);
    return threadMapper.toThread(response.data);
  } catch (error: unknown) {
    return rejectWithValue(toApiError(error, "Failed to change the thread"));
  }
});

/** Unread messages across the reader's threads, for the nav count. */
export const fetchUnreadCount = createAsyncThunk<
  number,
  MessagesChannel,
  { rejectValue: ApiErrorResponse }
>("messages/fetchUnreadCount", async (channel, { rejectWithValue }) => {
  try {
    const { http, base } = route(channel);
    const response = await http.get<UnreadCountResponse>(`${base}/unread-count`);
    return response.data.count;
  } catch (error: unknown) {
    return rejectWithValue(toApiError(error, "Failed to load the unread count"));
  }
});
